import React, { useState, useEffect } from "react";
import { Table, Tag, Button, Modal, Form, Input, Select, Space, App, Image, Descriptions, Divider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllKyc, verifyKyc } from "../../store/slices/kycSlice";
import dayjs from "dayjs";
import ExportButton from "../../Reusable/ExportButton/ExportButton";
import Loadingpage from "../Loadingpage/Loadingpage";


const KycManagement = () => {
    const { modal, message } = App.useApp();
    const dispatch = useDispatch();
    const { kycList, total, loading } = useSelector((state) => state.kyc);

    const [page, setPage] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({
        status: 'all',
        search: ''
    });

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [selectedKyc, setSelectedKyc] = useState(null);
    const [form] = Form.useForm();
    const watchStatus = Form.useWatch('status', form);
    const [searchTrigger, setSearchTrigger] = useState("");
    useEffect(() => {
        dispatch(fetchAllKyc({
            page: page.current,
            limit: page.pageSize,
            filter: filters.status !== 'all' ? filters.status : undefined,
            search: searchTrigger || undefined
        }));
    }, [dispatch, page.current, page.pageSize, filters.status, searchTrigger]);
    const handleVerify = async (values) => {
        try {
            const result = await dispatch(verifyKyc({
                id: selectedKyc._id,
                status: values.status,
                reason: values.reason || ""
            })).unwrap();

            if (result.success) {
                message.success(`KYC ${values.status} successfully`);
                setIsVerifyModalOpen(false);
                form.resetFields();
                dispatch(fetchAllKyc({
                    page: page.current,
                    limit: page.pageSize,
                    ...filters
                }));
            } else {
                message.error(result.message || 'Failed to update KYC');
            }
        } catch (err) {
            message.error(err || 'Failed to update KYC');
        }
    };

    const showConfirm = (values) => {
        modal.confirm({
            title: `Are you sure you want to ${values.status} this KYC?`,
            icon: values.status === 'approved' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
            content: `User's KYC status will be updated to ${values.status}.`,
            okText: 'Yes',
            cancelText: 'No',
            centered: true,
            onOk: () => handleVerify(values)
        });
    };

    const columns = [
        {
            title: "Sno",
            render: (_, __, i) => <span style={{ color: '#fff' }}>{(page.current - 1) * page.pageSize + i + 1}</span>
        },
        {
            title: "User",
            dataIndex: ["user_id", "email"],
            key: "user",
            render: (email) => <span style={{ color: '#fff' }}>{email || '-'}</span>
        },
        {
            title: "Full Name",
            dataIndex: "fullname",
            key: "fullname",
            render: (name) => <span style={{ color: '#fff' }}>{name || '-'}</span>
        },
        {
            title: "Country",
            dataIndex: "Country",
            key: "Country",
            render: (country) => <span style={{ color: '#fff' }}>{country || '-'}</span>
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                const statusMap = {
                    pending: { color: 'gold', text: 'PENDING' },
                    approved: { color: 'green', text: 'APPROVED' },
                    rejected: { color: 'red', text: 'REJECTED' }
                };
                const currentStatus = statusMap[status] || { color: 'default', text: 'UNKNOWN' };
                return <Tag color={currentStatus.color}>{currentStatus.text}</Tag>;
            }
        },
        {
            title: "Submitted At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => <span style={{ color: '#fff' }}>{dayjs(date).format('DD-MM-YYYY HH:mm')}</span>
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => {
                            setSelectedKyc(record);
                            setIsViewModalOpen(true);
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        icon={<CheckCircleOutlined />}
                        disabled={record.status === "approved"}
                        onClick={() => {
                            setSelectedKyc(record);
                            form.setFieldsValue({ status: 'approved' });
                            setIsVerifyModalOpen(true);
                        }}
                        style={{ color: record.status === 'approved' ? '#52c41a' : 'rgba(255, 255, 255, 0.3)' }}
                    >
                        Verify
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <div className="p-20 media-p-20">
            <Loadingpage />
            <div className="card-clr" style={{ padding: '20px', borderRadius: '16px', border: '1px solid #FC442080' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ color: '#fff', margin: 0 }}>KYC Management</h2>
                    {/* <div style={{ display: "flex", gap: "10px" }}>
                        <Button icon={<CloseCircleOutlined />} onClick={() => setFilters({ status: 'all', search: '' })}>Clear</Button>
                        <ExportButton
                            category="kyc"
                        />
                    </div> */}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <Input.Search
                            placeholder="Search User"
                            value={filters.search}
                            onChange={(e) => {
                                const value = e.target.value;

                                setFilters((f) => ({
                                    ...f,
                                    search: value,
                                }));

                                // clear icon click
                                if (!value) {
                                    setSearchTrigger("");

                                    setPage({
                                        current: 1,
                                        pageSize: 10,
                                    });
                                }
                            }}
                            onSearch={(v) => {
                                setSearchTrigger(v);

                                setPage((prev) => ({
                                    ...prev,
                                    current: 1,
                                }));
                            }}
                            style={{ width: 250 }}
                            allowClear
                            className="input-cls"
                        />
                        <Select
                            value={filters.status}
                            style={{ width: 140 }}
                            onChange={(v) => {
                                setFilters(f => ({ ...f, status: v }));
                                setPage({ current: 1, pageSize: 10 });
                            }}
                            className="selected-cls"
                        >
                            <Select.Option value="all">All Status</Select.Option>
                            <Select.Option value="pending">Pending</Select.Option>
                            <Select.Option value="approved">Approved</Select.Option>
                            <Select.Option value="rejected">Rejected</Select.Option>
                        </Select>
                    </div>
                    <div style={{ display: "flex", gap: "10px", marginBottom: 16, flexWrap: 'wrap' }}>
                        <Button icon={<CloseCircleOutlined />} onClick={() => {
                            setFilters({
                                status: 'all',
                                search: ''
                            });

                            setSearchTrigger("");

                            setPage({
                                current: 1,
                                pageSize: 10
                            });
                        }}>Clear</Button>
                        <ExportButton
                            category="kyc"
                        />
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={kycList}
                    loading={{
                        spinning: loading,
                        indicator: <Loadingpage isLoading={loading} />,
                    }}
                    pagination={{
                        total: total,
                        current: page.current,
                        pageSize: page.pageSize,
                        onChange: (p, s) => setPage({ current: p, pageSize: s }),
                        className: "pagination-clr"
                    }}
                    rowKey="_id"
                    className="table-clr"
                    scroll={{ x: 'max-content' }}
                />

                {/* View Details Modal */}
                <Modal
                    title="KYC Document Details"
                    open={isViewModalOpen}
                    onCancel={() => setIsViewModalOpen(false)}
                    footer={null}
                    width={850}
                    className="custom-modal"
                    centered
                >
                    {selectedKyc && (
                        <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: '12px' }}>
                            <Divider orientation="left" style={{ borderColor: '#FC442080', color: '#fff' }}>1. Personal Information</Divider>
                            <Descriptions bordered column={2} className="kyc-descriptions">
                                <Descriptions.Item label="Full Name">{selectedKyc.fullname}</Descriptions.Item>
                                <Descriptions.Item label="DOB">{selectedKyc.dob}</Descriptions.Item>
                                <Descriptions.Item label="Nationality">{selectedKyc.ID_country}</Descriptions.Item>
                                <Descriptions.Item label="ID Type">{selectedKyc.ID_type}</Descriptions.Item>
                                <Descriptions.Item label="ID Number">{selectedKyc.ID_Number}</Descriptions.Item>
                                <Descriptions.Item label="Expiry Date">{selectedKyc.expiry_date || '-'}</Descriptions.Item>
                            </Descriptions>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '15px' }}>
                                <div>
                                    <p style={{ color: '#fff', textAlign: 'center', fontSize: '11px' }}>ID Front</p>
                                    <Image src={selectedKyc.ID_Image_front} style={{ borderRadius: '8px', border: '1px solid #FC442080' }} />
                                </div>
                                <div>
                                    <p style={{ color: '#fff', textAlign: 'center', fontSize: '11px' }}>ID Back</p>
                                    <Image src={selectedKyc.ID_Image_Back} style={{ borderRadius: '8px', border: '1px solid #FC442080' }} />
                                </div>
                                <div>
                                    <p style={{ color: '#fff', textAlign: 'center', fontSize: '11px' }}>Selfie</p>
                                    <Image src={selectedKyc.selfie_image} style={{ borderRadius: '8px', border: '1px solid #FC442080' }} />
                                </div>
                            </div>

                            <Divider orientation="left" style={{ borderColor: '#FC442080', color: '#fff' }}>2. Address Verification</Divider>
                            <Descriptions bordered column={2} className="kyc-descriptions">
                                <Descriptions.Item label="Address" span={2}>{selectedKyc.address}</Descriptions.Item>
                                <Descriptions.Item label="City">{selectedKyc.city}</Descriptions.Item>
                                <Descriptions.Item label="State">{selectedKyc.stateOrProvince}</Descriptions.Item>
                                <Descriptions.Item label="Country">{selectedKyc.country || selectedKyc.Country}</Descriptions.Item>
                                <Descriptions.Item label="Pincode">{selectedKyc.postalcode}</Descriptions.Item>
                                <Descriptions.Item label="Proof Type" span={2}>{selectedKyc.address_proof}</Descriptions.Item>
                            </Descriptions>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '15px' }}>
                                <div>
                                    <p style={{ color: '#fff', textAlign: 'center', fontSize: '11px' }}>Address Proof Front</p>
                                    <Image src={selectedKyc.address_front_image} style={{ borderRadius: '8px', border: '1px solid #FC442080' }} />
                                </div>
                                <div>
                                    <p style={{ color: '#fff', textAlign: 'center', fontSize: '11px' }}>Address Proof Back</p>
                                    <Image src={selectedKyc.address_back_image} style={{ borderRadius: '8px', border: '1px solid #FC442080' }} />
                                </div>
                            </div>

                            <Divider orientation="left" style={{ borderColor: '#FC442080', color: '#fff' }}>3. Financial & Source of Funds</Divider>
                            <Descriptions bordered column={2} className="kyc-descriptions">
                                <Descriptions.Item label="Status">{selectedKyc.empyType}</Descriptions.Item>
                                <Descriptions.Item label="Occupation">{selectedKyc.occupation}</Descriptions.Item>
                                <Descriptions.Item label="Income">{selectedKyc.income}</Descriptions.Item>
                                <Descriptions.Item label="Exp. Deposit">{selectedKyc.expected_deposit}</Descriptions.Item>
                                <Descriptions.Item label="Source">{selectedKyc.source}</Descriptions.Item>
                                <Descriptions.Item label="Origin of Funds">{selectedKyc.country_funds}</Descriptions.Item>
                            </Descriptions>

                            {selectedKyc.support_doc && (
                                <div style={{ marginTop: '15px' }}>
                                    <p style={{ color: '#fff', fontSize: '11px' }}>Supporting Document</p>
                                    <Image src={selectedKyc.support_doc} style={{ borderRadius: '8px', border: '1px solid #FC442080', maxWidth: '200px' }} />
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                {/* Verify Modal */}
                <Modal
                    title="Verify KYC"
                    open={isVerifyModalOpen}
                    onCancel={() => {
                        setIsVerifyModalOpen(false);
                        form.resetFields();
                    }}
                    onOk={() => form.submit()}
                    className="custom-modal"
                    centered
                >
                    <Form form={form} layout="vertical" onFinish={showConfirm}>
                        <Form.Item name="status" label="Action" rules={[{ required: true }]}>
                            <Select>
                                <Select.Option value="approved">Approve</Select.Option>
                                <Select.Option value="rejected">Reject</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item name="reason" label="Remark / Rejection Reason" required={watchStatus === 'rejected'}>
                            <Input.TextArea placeholder="Enter notes or rejection reason" rows={4} />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
            <style>{`
        .kyc-descriptions .ant-descriptions-item-label {
            background-color: rgba(252, 68, 32, 0.1) !important;
            color: #fff !important;
            font-weight: 600;
        }
        .kyc-descriptions .ant-descriptions-item-content {
            color: rgba(255, 255, 255, 0.85) !important;
            background-color: rgba(0, 0, 0, 0.2) !important;
        }
      `}</style>
        </div>
    );
};

export default KycManagement;
